import React, {
  createContext, useCallback, useContext, useEffect, useMemo, useRef, useState,
} from 'react';
import {
  FiAlertCircle, FiArrowDown, FiCheck, FiCornerUpLeft, FiEdit2, FiEye, FiLock, FiLogOut, FiPlus, FiSearch, FiTrash2,
  FiUserPlus, FiUsers, FiX,
} from 'react-icons/fi';
import {
  cancelGroupChatQuestion,
  createGroupChat,
  deleteConversation,
  deleteGroupChat,
  getGroupChatOriginalCopy,
  keepGroupChatOriginalCopy,
  leaveGroupChat,
  listProjectGroupChats,
  listProjectMembers,
  markProjectGroupChatsSeen,
  pollGroupChatActivity,
  promoteConversationToGroup,
  renameGroupChat,
  setGroupChatMembers,
  setGroupChatTyping,
} from '../utils/conversationApi';
import FocusModeTag from './FocusModeTag';
import { focusModeTagMatches } from '../utils/focusModes';
import './GroupChat.css';

/**
 * Group chats: one conversation inside a shared project that its members can
 * read and ask KAI in -- everyone in the project, or only the people whoever
 * started it chose. See .claude/plan/plan_multi-user-conversation.md and
 * backend/services/group_chat_service.py.
 *
 * Everything the chat screen (ChatInterface.js) and the project panel
 * (ProjectPanel.js) show for one lives here: who wrote each message, the
 * live poll that brings in other people's questions and KAI's answers, the
 * queue of questions waiting for KAI, and the panel's own list of chats.
 */

// ── Who ──────────────────────────────────────────────────────────────────────

/** The first letter of someone's name, as the project's own avatars show it ("Gates, Bill" -> G). */
export function initialsOf(name) {
  const text = String(name || '').trim();
  return text ? text.charAt(0).toUpperCase() : '?';
}

/**
 * Who wrote a user message in a group chat: its stored author; or, with
 * none, either our own message not saved yet (no message_index) or one from
 * before it became a group chat -- every one of those is its creator's, but
 * in a copy of someone's shared conversation (imported_from) the ones up to
 * where it was copied are that person's.
 */
export function authorOf(message, group) {
  const me = group?.my_user_id;
  const author = message?.author;
  if (author?.user_id) {
    return {
      userId: author.user_id,
      name: author.display_name || 'A member',
      isMe: author.user_id === me,
    };
  }
  if (message?.message_index === undefined) return { userId: me, name: 'You', isMe: true };
  const origin = group?.imported_from;
  if (origin?.user_id && message.message_index <= origin.through_index) {
    return { userId: origin.user_id, name: origin.display_name || 'A member', isMe: origin.user_id === me };
  }
  const creator = group?.created_by || {};
  return { userId: creator.user_id, name: creator.display_name || 'A member', isMe: creator.user_id === me };
}

/** A directory photo URL worth showing, or null -- the directory says "N/A" for none. */
function usablePhoto(url) {
  const text = typeof url === 'string' ? url.trim() : '';
  return text && text.toUpperCase() !== 'N/A' ? text : null;
}

// Everyone's KPF directory photo (ws.kpf.com), by user_id, per project. The
// project's members list already carries one per person, the viewer's own
// included (project_routes._attach_profile_photos), so that's where they come
// from. Shared by every avatar on screen and kept a few minutes, so opening
// another chat doesn't ask again; a failed load isn't kept.
const PHOTOS_TTL_MS = 5 * 60 * 1000;
const photoCache = new Map();

function loadMemberPhotos(projectId) {
  const cached = photoCache.get(projectId);
  if (cached && Date.now() - cached.at < PHOTOS_TTL_MS) return cached.promise;
  const promise = listProjectMembers(projectId).then((members) => {
    const photos = {};
    members.forEach((m) => {
      const photo = usablePhoto(m.profile_img);
      if (m.user_id && photo) photos[m.user_id] = photo;
    });
    return photos;
  });
  photoCache.set(projectId, { at: Date.now(), promise });
  promise.catch(() => {
    if (photoCache.get(projectId)?.promise === promise) photoCache.delete(projectId);
  });
  return promise;
}

const NO_PHOTOS = {};
const PhotosContext = createContext(NO_PHOTOS);

/** Gives the avatars inside it the photos of projectId's members. */
export function MemberPhotosProvider({ projectId, children }) {
  const [photos, setPhotos] = useState(NO_PHOTOS);
  useEffect(() => {
    setPhotos(NO_PHOTOS);
    if (!projectId) return undefined;
    let live = true;
    loadMemberPhotos(projectId).then((map) => { if (live) setPhotos(map); }, () => {});
    return () => { live = false; };
  }, [projectId]);
  return <PhotosContext.Provider value={photos}>{children}</PhotosContext.Provider>;
}

/**
 * Someone's directory photo -- `photo` if given, else theirs from the
 * MemberPhotosProvider around it -- or their initials in their colour when
 * there's none, or it fails to load (a URL on file can still 404).
 */
export function Avatar({ userId, name, size = 22, title, photo }) {
  const photos = useContext(PhotosContext);
  const src = usablePhoto(photo) || photos[userId] || null;
  const [failedSrc, setFailedSrc] = useState(null);
  if (src && src !== failedSrc) {
    return (
      <img
        src={src}
        alt={title ? (name || '') : ''}
        referrerPolicy="no-referrer"
        className="gc-avatar gc-avatar--photo"
        style={{ width: size, height: size }}
        title={title || name}
        aria-hidden={title ? undefined : 'true'}
        onError={() => setFailedSrc(src)}
      />
    );
  }
  return (
    <span
      className="gc-avatar"
      style={{ width: size, height: size, fontSize: Math.max(9, Math.round(size * 0.4)) }}
      title={title || name}
      aria-hidden={title ? undefined : 'true'}
    >
      {initialsOf(name)}
    </span>
  );
}

export function AvatarStack({ people, max = 4, size = 22 }) {
  const shown = (people || []).slice(0, max);
  const more = (people || []).length - shown.length;
  if (shown.length === 0) return null;
  return (
    <span
      className="gc-avatar-stack"
      style={{ '--gc-overlap': `-${Math.round(size / 3)}px` }}
      aria-label={(people || []).map((p) => p.display_name).join(', ')}
    >
      {shown.map((p) => (
        <Avatar key={p.user_id || p.display_name} userId={p.user_id} name={p.display_name} size={size}
          title={p.display_name} photo={p.profile_img} />
      ))}
      {more > 0 && <span className="gc-avatar gc-avatar-more" style={{ width: size, height: size }}>+{more}</span>}
    </span>
  );
}

// A request that never got an answer -- the server restarting, a dropped
// connection -- reaches us as fetch's own TypeError, "Failed to fetch".
function friendlyError(error, fallback) {
  const message = error?.message || '';
  if (error instanceof TypeError || /failed to fetch|networkerror|load failed/i.test(message)) {
    return "Couldn't reach KAI just now.";
  }
  return message || fallback;
}

function relativeTime(iso) {
  if (!iso) return '';
  const then = new Date(iso).getTime();
  if (Number.isNaN(then)) return '';
  const minutes = Math.round((Date.now() - then) / 60000);
  if (minutes < 1) return 'now';
  if (minutes < 60) return `${minutes}m`;
  const hours = Math.round(minutes / 60);
  if (hours < 24) return `${hours}h`;
  const days = Math.round(hours / 24);
  if (days < 7) return `${days}d`;
  return new Date(iso).toLocaleDateString();
}

// ── A copy of a chat you're no longer in ─────────────────────────────────────
// Leaving a group chat, being taken off it, or losing its project leaves a
// read-only copy of it as it stood (left_group on the conversation, see
// conversation_service.save_group_chat_copy): readable, never asked in again.

/** Why you have the copy, for its row in a list: "You left", "Removed by Priya". */
export function leftGroupLabel(left) {
  const by = left?.by?.display_name;
  switch (left?.reason) {
    case 'removed_from_chat': return by ? `Removed by ${by}` : 'Removed';
    case 'left_project': return 'You left the project';
    case 'removed_from_project': return 'Removed from the project';
    case 'project_deleted': return 'Project deleted';
    default: return 'You left';
  }
}

/** The same as a sentence, for the bar the copy shows in place of its composer. */
export function leftGroupText(left) {
  const at = left?.at ? new Date(left.at) : null;
  const on = at && !Number.isNaN(at.getTime())
    ? ` on ${at.toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric' })}` : '';
  const by = left?.by?.display_name;
  switch (left?.reason) {
    case 'removed_from_chat':
      return by ? `${by} removed you from this group chat${on}.` : `You were removed from this group chat${on}.`;
    case 'left_project': return `You left this group chat's project${on}.`;
    case 'removed_from_project': return `You were removed from this group chat's project${on}.`;
    case 'project_deleted': return `This group chat's project was deleted${on}.`;
    default: return `You left this group chat${on}.`;
  }
}

/** In place of the composer, in a copy of a chat you're no longer in. */
export function LeftGroupChatBar({ left }) {
  return (
    <div className="gc-left-bar" role="status">
      <FiLock size={14} aria-hidden="true" />
      <span>
        {leftGroupText(left)} This is your copy of the chat as it was then: you can read it, but not ask KAI here.
        {left?.reason !== 'project_deleted' && (
          left?.reason === 'left_project' || left?.reason === 'removed_from_project'
            ? " If you're back in the project, anyone still in the chat can add you back."
            : ' Anyone still in the chat can add you back.'
        )}
      </span>
    </div>
  );
}

/** In place of the composer, in a group chat someone continued as a new
 * one (continued_in): it's closed for everyone, with a way to the new one. */
export function ContinuedGroupChatBar({ continued, myUserId, onOpen }) {
  const by = continued?.by?.user_id && continued.by.user_id === myUserId
    ? 'You' : (continued?.by?.display_name || 'Someone');
  const where = continued?.name ? `"${continued.name}"` : 'a new group chat';
  return (
    <div className="gc-left-bar gc-continued-bar" role="status">
      <FiLock size={14} aria-hidden="true" />
      <span>
        {by} continued this chat in {where}. It&apos;s closed now: everyone can still read it, but questions
        go in the new chat.
      </span>
      {continued?.conversation_id && onOpen && (
        <button type="button" className="gc-continued-open" onClick={() => onOpen(continued.conversation_id)}>
          Open the new chat →
        </button>
      )}
    </div>
  );
}

// ── The live chat ────────────────────────────────────────────────────────────

/**
 * Fold a poll's new messages into what's on screen. Messages already shown
 * (by message_index) are replaced, and our own optimistic copy of a turn --
 * the question we typed, KAI's streamed answer -- gives way to the stored
 * one once it arrives (matched by the question's pending_id). Anything else
 * not yet stored (a question still queued, an error) stays at the end.
 *
 * A stored message takes the id of the copy it replaces: the id is its row's
 * key in the transcript, and a new key draws the row afresh -- re-measured,
 * its images reloaded -- which moves everything at the bottom up and down.
 */
export function mergeGroupMessages(prev, incoming) {
  if (!incoming || incoming.length === 0) return prev;
  const incomingIndexes = new Set(incoming.map((m) => m.message_index).filter((i) => i !== undefined));
  const incomingPendingIds = new Set(
    incoming.filter((m) => m.role === 'user' && m.pending_id).map((m) => m.pending_id),
  );
  const replacedTurns = new Set(
    prev
      .filter((m) => m.role === 'user' && m.localTurnId && m.pendingId && incomingPendingIds.has(m.pendingId))
      .map((m) => m.localTurnId),
  );
  // The ids on screen now: by message_index for stored copies, and by the
  // question's pending_id for our own turns ({ question, answer }).
  const idByIndex = new Map();
  const turnIds = new Map();
  const stored = [];
  const local = [];
  prev.forEach((m) => {
    if (m.localTurnId && replacedTurns.has(m.localTurnId)) {
      const ids = turnIds.get(m.localTurnId) || {};
      const slot = m.role === 'user' ? 'question' : 'answer';
      if (ids[slot] == null) ids[slot] = m.id;
      if (m.role === 'user') ids.pendingId = m.pendingId;
      turnIds.set(m.localTurnId, ids);
      return;
    }
    if (m.message_index !== undefined) {
      if (!incomingIndexes.has(m.message_index)) stored.push(m);
      else idByIndex.set(m.message_index, m.id);
    } else {
      local.push(m);
    }
  });
  const idsByPendingId = new Map([...turnIds.values()].map((ids) => [ids.pendingId, ids]));
  let answerId = null;
  const arrived = incoming
    .slice()
    .sort((a, b) => (a.message_index ?? 0) - (b.message_index ?? 0))
    .map((m) => {
      let id = idByIndex.get(m.message_index);
      if (m.role === 'user') {
        const ids = m.pending_id ? idsByPendingId.get(m.pending_id) : null;
        id = id ?? ids?.question;
        // KAI's answer to it is the next one from KAI.
        answerId = ids?.answer ?? null;
      } else if (m.role === 'assistant' && answerId != null) {
        id = id ?? answerId;
        answerId = null;
      }
      if (id == null) id = m.message_index !== undefined ? `g${m.message_index}` : m.id;
      return { ...m, id };
    });
  return [...stored, ...arrived, ...local];
}

function lastStoredIndex(messages, fallback) {
  let max = fallback ?? -1;
  (messages || []).forEach((m) => {
    if (typeof m.message_index === 'number' && m.message_index > max) max = m.message_index;
  });
  return max;
}

const ACTIVE_POLL_MS = 2000;
const IDLE_POLL_MS = 5000;
const HIDDEN_POLL_MS = 20000;

/**
 * Keep an open group chat live: poll its activity (one small read while
 * nothing changes), bring in other people's messages, and track the queue
 * and whose turn it is.
 *
 * holdScrollRef: set just before messages from someone else are added while
 * the reader is scrolled up, so ChatInterface's "scroll to the new message"
 * leaves them where they are -- the "N new messages" pill shows instead.
 *
 * onGone: the chat isn't there any more -- () => Promise<boolean>, true when
 * it has reopened as the reader's own conversation (everyone else left it).
 */
export function useGroupChatActivity({
  conversation, group, messagesRef, setMessages, onReload, isNearBottom, holdScrollRef, onRenamed, onGone,
  onFocusMode, onKnowledgeScope,
}) {
  const conversationId = group ? conversation?.conversation_id : null;
  const projectId = conversation?.project_id || null;
  const [pending, setPending] = useState(group?.pending || []);
  const [turn, setTurn] = useState(group?.turn || null);
  const [newMessages, setNewMessages] = useState(0);
  const [gone, setGone] = useState(false);
  // Who's in the chat, once a poll has said -- null until then (the
  // group's own list stands).
  const [people, setPeople] = useState(null);
  // Everyone else typing in it right now.
  const [typing, setTyping] = useState([]);
  // What KAI has written so far of someone else's answer ({pending_id, text,
  // truncated, stick}) -- null unless it's answering someone else.
  const [live, setLive] = useState(null);
  // Where the chat went on, once someone continued it as a new group chat
  // ({conversation_id, name, by}) -- it's closed for everyone then.
  const [continuedIn, setContinuedIn] = useState(group?.continued_in || null);
  // Bumped when someone else's question shows up below the transcript
  // while the reader is at the bottom: the screen scrolls down to it.
  const [reveal, setReveal] = useState(0);
  // Other people's questions already scrolled to or counted in the pill,
  // so the same one isn't counted again once it's answered.
  const noticedRef = useRef(new Set());
  const cursorRef = useRef({ seq: group?.activity_seq ?? null, version: group?.history_version ?? 0 });
  const onReloadRef = useRef(onReload);
  onReloadRef.current = onReload;
  const onRenamedRef = useRef(onRenamed);
  onRenamedRef.current = onRenamed;
  const onFocusModeRef = useRef(onFocusMode);
  onFocusModeRef.current = onFocusMode;
  const onKnowledgeScopeRef = useRef(onKnowledgeScope);
  onKnowledgeScopeRef.current = onKnowledgeScope;
  const onGoneRef = useRef(onGone);
  onGoneRef.current = onGone;
  const isNearBottomRef = useRef(isNearBottom);
  isNearBottomRef.current = isNearBottom;
  const groupRef = useRef(group);
  groupRef.current = group;
  const pollNowRef = useRef(() => {});

  // A fresh load of the chat (opening it, or a reload after a repair)
  // restarts the poll from what that load saw.
  useEffect(() => {
    if (!group) return;
    cursorRef.current = { seq: group.activity_seq ?? null, version: group.history_version ?? 0 };
    setPending(group.pending || []);
    setTurn(group.turn || null);
    setGone(false);
    setPeople(null);
    setTyping([]);
    setLive(null);
    setContinuedIn(group.continued_in || null);
    noticedRef.current = new Set((group.pending || []).map((p) => p.pending_id));
  }, [group]);

  useEffect(() => {
    if (!conversationId) return undefined;
    let cancelled = false;
    let timer = null;
    let failures = 0;
    let busy = false;
    let active = Boolean(groupRef.current?.turn) || (groupRef.current?.pending || []).length > 0;

    const schedule = () => {
      if (cancelled) return;
      clearTimeout(timer);
      const hidden = typeof document !== 'undefined' && document.visibilityState === 'hidden';
      const base = hidden ? HIDDEN_POLL_MS : (active ? ACTIVE_POLL_MS : IDLE_POLL_MS);
      timer = setTimeout(tick, failures ? Math.min(30000, base * 2 ** failures) : base);
    };

    const tick = async () => {
      if (cancelled || busy) return;
      busy = true;
      try {
        const focused = typeof document !== 'undefined'
          && document.visibilityState === 'visible' && document.hasFocus();
        const result = await pollGroupChatActivity(conversationId, {
          projectId,
          sinceSeq: cursorRef.current.seq,
          sinceIndex: lastStoredIndex(messagesRef.current, groupRef.current?.last_message_index),
          historyVersion: cursorRef.current.version,
          seen: focused,
        });
        if (cancelled) return;
        failures = 0;
        cursorRef.current = { seq: result.activity_seq, version: result.history_version };
        setTurn(result.turn || null);
        const typingNow = result.typing || [];
        setTyping((prev) => (sameTypers(prev, typingNow) ? prev : typingNow));
        const liveNow = result.live && result.live.text ? result.live : null;
        setLive((prev) => {
          if (!liveNow) return null;
          if (prev && prev.pending_id === liveNow.pending_id && prev.text === liveNow.text) return prev;
          // Whether the reader was at the bottom before it grew: then it stays in view.
          return { ...liveNow, stick: isNearBottomRef.current ? isNearBottomRef.current() : true };
        });
        if (result.members) {
          setPeople({
            members: result.members,
            everyone: result.everyone !== false,
            manager: result.manager || null,
            rejoined: result.rejoined || [],
            canManagePeople: Boolean(result.can_manage_people),
            canAddPeople: Boolean(result.can_add_people),
          });
        }
        // Someone switched the chat's focus mode (or its first question set it).
        if (result.focus_mode) {
          onFocusModeRef.current?.(conversationId, {
            id: result.focus_mode, name: result.focus_mode_name || result.focus_mode,
          });
        }
        // Renamed by someone (or named by its first question).
        if (result.name) onRenamedRef.current?.(conversationId, result.name);
        // Its knowledge base changed (null: back to all files).
        if ('knowledge_scope' in result) onKnowledgeScopeRef.current?.(conversationId, result.knowledge_scope || null);
        // Someone continued it as a new group chat: it closes here too.
        if ('continued_in' in result) {
          const next = result.continued_in || null;
          setContinuedIn((prev) => (prev?.conversation_id === next?.conversation_id ? prev : next));
        }
        const me = groupRef.current?.my_user_id;
        let queue = null;
        if (result.pending) {
          queue = result.pending;
          setPending(queue);
          // Someone else's question, new below the transcript: shown at
          // once if the reader is at the bottom, else counted in the pill.
          const arrived = queue.filter((p) => (
            p.status !== 'not_sent' && p.author?.user_id !== me && !noticedRef.current.has(p.pending_id)
          ));
          if (arrived.length > 0) {
            arrived.forEach((p) => noticedRef.current.add(p.pending_id));
            const nearBottom = isNearBottomRef.current ? isNearBottomRef.current() : true;
            if (nearBottom) setReveal((n) => n + 1);
            else setNewMessages((n) => n + arrived.length);
          }
        }
        // Someone typing is about to ask: look more often, so their question
        // (and the next "is typing") shows sooner.
        active = Boolean(result.turn) || typingNow.length > 0
          || (queue || []).some((p) => p.status !== 'not_sent');
        if (result.reload) {
          onReloadRef.current?.();
        } else if (result.messages && result.messages.length > 0) {
          const fromOthers = result.messages.filter(
            (m) => m.role === 'user' && m.author?.user_id && m.author.user_id !== me,
          );
          // Questions already counted while they waited or were answered.
          const counted = fromOthers.filter((m) => m.pending_id && noticedRef.current.has(m.pending_id));
          const nearBottom = isNearBottomRef.current ? isNearBottomRef.current() : true;
          if (!nearBottom && holdScrollRef) {
            holdScrollRef.current = true;
            const uncounted = fromOthers.length - counted.length;
            if (uncounted > 0 || counted.length === 0) setNewMessages((n) => n + Math.max(1, uncounted));
          } else if (counted.length > 0 && holdScrollRef) {
            // Someone else's answer the reader watched grow at the bottom,
            // now stored in place of that copy: they stay at the bottom,
            // rather than being taken back up to where the answer starts.
            holdScrollRef.current = true;
            setReveal((n) => n + 1);
          }
          setMessages((prev) => mergeGroupMessages(prev, result.messages));
        }
      } catch (error) {
        if (error.status === 404) {
          // Everyone else left, so it's the reader's own again -- or it was
          // deleted, or they've lost access to the project.
          cancelled = true;
          const reopened = onGoneRef.current ? await onGoneRef.current() : false;
          if (!reopened) setGone(true);
          return;
        }
        failures += 1;
      } finally {
        busy = false;
      }
      schedule();
    };

    pollNowRef.current = () => {
      clearTimeout(timer);
      tick();
    };
    const onVisible = () => {
      if (document.visibilityState === 'visible') pollNowRef.current();
    };
    document.addEventListener('visibilitychange', onVisible);
    window.addEventListener('focus', onVisible);
    schedule();
    return () => {
      cancelled = true;
      clearTimeout(timer);
      document.removeEventListener('visibilitychange', onVisible);
      window.removeEventListener('focus', onVisible);
    };
  }, [conversationId, projectId, messagesRef, setMessages, holdScrollRef]);

  const pollNow = useCallback(() => pollNowRef.current(), []);
  const clearNewMessages = useCallback(() => setNewMessages(0), []);

  const cancelQuestion = useCallback(async (pendingId) => {
    if (!conversationId) return;
    await cancelGroupChatQuestion(conversationId, pendingId);
    setPending((prev) => prev.filter((p) => p.pending_id !== pendingId));
    pollNow();
  }, [conversationId, pollNow]);

  return {
    pending, turn, newMessages, clearNewMessages, cancelQuestion, pollNow, gone, people, typing, live, reveal,
    continuedIn,
  };
}

function sameTypers(a, b) {
  return a.length === b.length && a.every((p, i) => p.user_id === b[i].user_id);
}

// How often the composer says "still typing" while its text keeps changing
// -- well inside the server's TYPING_SECONDS, so the line doesn't flicker.
const TYPING_PING_MS = 3000;

/**
 * Let everyone else in a group chat see we're typing: told when the
 * composer's text changes (at most every few seconds), and that we've
 * stopped when it's emptied -- sent, or cleared -- or we leave the chat. Just
 * pausing lets it lapse on its own. ``conversationId``: null outside a group
 * chat, where nothing is sent.
 */
export function useTypingSignal({ conversationId, projectId, text }) {
  const textRef = useRef(text);
  textRef.current = text;
  const lastTextRef = useRef(text);
  const sentAtRef = useRef(0);

  // First, so a chat's own draft coming in with it doesn't count as typing.
  useEffect(() => {
    lastTextRef.current = textRef.current;
    if (!conversationId) return undefined;
    return () => {
      if (!sentAtRef.current) return;
      sentAtRef.current = 0;
      setGroupChatTyping(conversationId, { projectId, typing: false }).catch(() => {});
    };
  }, [conversationId, projectId]);

  useEffect(() => {
    const previous = lastTextRef.current;
    lastTextRef.current = text;
    if (!conversationId || text === previous) return;
    if (!String(text || '').trim()) {
      if (!sentAtRef.current) return;
      sentAtRef.current = 0;
      setGroupChatTyping(conversationId, { projectId, typing: false }).catch(() => {});
      return;
    }
    const now = Date.now();
    if (now - sentAtRef.current < TYPING_PING_MS) return;
    sentAtRef.current = now;
    setGroupChatTyping(conversationId, { projectId, typing: true }).catch(() => {});
  }, [text, conversationId, projectId]);
}

/** "Gates, Bill is typing", "… and … are typing", "3 people are typing". */
export function typingText(people) {
  const names = (people || []).map((p) => p.display_name || 'A member');
  if (names.length === 0) return '';
  if (names.length === 1) return `${names[0]} is typing`;
  if (names.length === 2) return `${names[0]} and ${names[1]} are typing`;
  return `${names.length} people are typing`;
}

/** Who else is typing, just above the composer, as Teams shows it: their faces, then "Gates, Bill is typing ···". */
export function GroupChatTyping({ typing }) {
  if (!typing || typing.length === 0) return null;
  return (
    <div className="gc-typing gc-typing--people" role="status" aria-live="polite">
      <AvatarStack people={typing} max={3} size={20} />
      <span className="gc-typing-text">{typingText(typing)}</span>
      <span className="gc-dots gc-dots--muted" aria-hidden="true"><span /><span /><span /></span>
    </div>
  );
}

// The transcript notes where the people in a chat changed (group_chat_service's
// "Notes about who's in a chat").
const MEMBER_NOTE_TYPES = new Set([
  'group_member_left', 'group_member_left_project', 'group_member_rejoined_project', 'group_members_changed',
  'group_chat_made', 'group_focus_mode',
]);

export function isMemberNote(message) {
  return MEMBER_NOTE_TYPES.has(message?.additional_data?.type);
}

function joinNames(names) {
  if (names.length <= 1) return names.join('');
  return `${names.slice(0, -1).join(', ')} and ${names[names.length - 1]}`;
}

/** A member note's line, as this reader sees it: "You added Priya", "Aaron added you". */
export function memberNoteText(data, myUserId) {
  const by = data.by?.user_id && data.by.user_id === myUserId ? 'You' : (data.by?.display_name || 'Someone');
  const names = (people) => joinNames((people || []).map(
    (p) => (p.user_id && p.user_id === myUserId ? 'you' : (p.display_name || 'someone')),
  ));
  if (data.type === 'group_member_left') {
    return data.display_name ? `${data.display_name} left the chat.` : 'Someone left the chat.';
  }
  if (data.type === 'group_member_left_project') {
    // A removal doesn't say who did it.
    return `${data.display_name || 'Someone'} ${data.removed ? 'was removed from' : 'left'} the project.`;
  }
  if (data.type === 'group_member_rejoined_project') {
    const who = data.user_id && data.user_id === myUserId ? 'You' : (data.display_name || 'Someone');
    return `${who} rejoined the project.`;
  }
  if (data.type === 'group_focus_mode') {
    return `${by} switched this chat to ${data.to_name || 'another focus mode'}.`;
  }
  if (data.type === 'group_chat_made') {
    return data.everyone
      ? `${by} made this a group chat for everyone in the project.`
      : `${by} made this a group chat with ${names(data.members)}.`;
  }
  if (data.everyone) return `${by} opened the chat to everyone in the project.`;
  if (data.limited_to) return `${by} limited the chat to ${names(data.limited_to)}.`;
  const parts = [];
  if ((data.added || []).length) parts.push(`added ${names(data.added)}`);
  if ((data.removed || []).length) parts.push(`removed ${names(data.removed)}`);
  return parts.length ? `${by} ${parts.join(' and ')}.` : `${by} changed who's in the chat.`;
}

/** The note where someone continued this chat as a new group chat: "Aaron continued this chat in …". */
export function continuedNoteText(data, myUserId) {
  const by = data.by?.user_id && data.by.user_id === myUserId ? 'You' : (data.by?.display_name || 'Someone');
  return data.name
    ? `${by} continued this chat in a new group chat, "${data.name}".`
    : `${by} continued this chat in a new group chat.`;
}

/**
 * A group chat's "Knowledge base updated" note, naming who changed it -- anyone
 * in the chat can choose which files it searches, for everyone in it:
 * "Knowledge base updated by Priya." Null for a note without ``by`` (one in
 * your own conversation), which keeps the plain wording.
 */
export function knowledgeScopeNoteText(data, myUserId) {
  if (!data?.by) return null;
  const by = data.by.user_id && data.by.user_id === myUserId ? 'you' : (data.by.display_name || 'someone');
  return `Knowledge base updated by ${by}. Later answers reflect this.`;
}

/**
 * The group chat this conversation object is, or null -- keeping the last
 * known details when a route that doesn't carry them (a rename, a model
 * change) hands back the conversation without them.
 */
export function useStableGroup(conversation) {
  const lastRef = useRef({ id: null, group: null });
  if (!conversation?.is_group) return null;
  if (conversation.group) {
    lastRef.current = { id: conversation.conversation_id, group: conversation.group };
    return conversation.group;
  }
  return lastRef.current.id === conversation.conversation_id ? lastRef.current.group : null;
}

/**
 * What "Invite people" in a group chat opens the project's members with:
 * the project, plus the chat it was opened from (ProjectMembersModal's
 * ``inviteToChat``), so an invite from a chat that's everyone's in the
 * project emails a link straight to it.
 */
export function projectForChatInvite(project, conversation, group) {
  if (!project) return null;
  return {
    ...project,
    inviteToChat: group && conversation?.conversation_id
      ? {
        conversationId: conversation.conversation_id,
        name: conversation.name,
        everyone: group.everyone !== false,
      }
      : null,
  };
}

// ── Chat screen pieces ───────────────────────────────────────────────────────

/**
 * The chat header's group chat bits: the pill, who's in it (a button that
 * opens GroupChatPeopleModal), and an invite. ``people``: the poll's latest
 * word on who's in it, which wins over the group's own.
 */
export function GroupChatHeaderBits({ group, people = null, onInvite, onShowPeople }) {
  if (!group) return null;
  const members = people?.members || group.members;
  // 30px, the size of the project header's faces (.project-panel-avatar).
  const stack = <AvatarStack people={members} max={4} size={30} />;
  return (
    <span className="gc-header-bits">
      <span
        className="gc-pill"
        title="Only the people in this chat can read it and ask KAI here. Nobody else in the project can see it."
      >
        <FiUsers size={12} /> Group chat
      </span>
      {onShowPeople ? (
        <button type="button" className="gc-people-btn" onClick={onShowPeople} title="Who's in this chat">
          {stack}
        </button>
      ) : stack}
      {onInvite && (
        <button type="button" className="gc-invite-btn" onClick={onInvite} title="Invite people to this project">
          <FiUserPlus size={14} />
          <span className="gc-invite-label">Invite people</span>
        </button>
      )}
    </span>
  );
}

function kaiLogo() {
  return window.location.pathname.includes('/proxy/') ? '/proxy/3000/KAI.png' : '/KAI.png';
}

/**
 * "KAI is answering Priya's question", above the composer. ``answerShown``:
 * the transcript already draws that answer, headed "answering Priya", so
 * saying it again here is left out.
 */
export function GroupChatTurnBanner({ turn, group, answerShown = false }) {
  if (!turn || !group || answerShown || turn.holder_user_id === group.my_user_id) return null;
  // Laid out like the typing line below it: KAI's logo, the words, dots.
  return (
    <div className="gc-typing" role="status" aria-live="polite">
      <img src={kaiLogo()} alt="" aria-hidden="true" className="gc-avatar gc-avatar--photo"
        style={{ width: 22, height: 22 }} />
      <span className="gc-typing-text">KAI is answering {turn.holder_name || 'someone'}&apos;s question</span>
      <span className="gc-dots gc-dots--muted" aria-hidden="true"><span /><span /><span /></span>
    </div>
  );
}

/**
 * Questions waiting in the queue -- other people's, and any of ours this
 * screen isn't already showing (e.g. one that wasn't sent because we left).
 * ``live``: what KAI has written so far of the one it's answering (the
 * poll's). ``renderRunning(item, live)``: the chat screen draws that one as
 * the question and answer it becomes, so nothing changes look when the
 * answer's done; without it, a card with the answer as plain text.
 */
/** The caption over a reply's quote. A question quoted in a group chat may be anyone's. */
export function replyLabel(replyContext, inGroupChat) {
  if (replyContext?.role === 'assistant') return 'Replying to KAI';
  if (replyContext?.role === 'user') return inGroupChat ? 'Replying to a question' : 'Replying to your message';
  return 'Replying to selected excerpt';
}

/** A waiting question's quote (reply_context), in the shape a message carries it. */
export function pendingReplyContext(item) {
  const rc = item?.reply_context;
  return rc?.text ? { text: rc.text, role: rc.role, isFullMessage: !!rc.is_full_message } : null;
}

// A waiting or running question's files, shaped as a message's own `files`
// (null for none). Older rows only say there were some: has_attachments.
export function pendingFiles(item) {
  const list = (item?.attachments || []).filter((a) => a?.name);
  return list.length ? list.map((a) => ({ name: a.name, type: a.type || '', tokens: a.tokens })) : null;
}

/**
 * Our own turns that belong below the queue rather than in the transcript:
 * a question still waiting for KAI, or one sent or started while someone
 * else's answer is still drawn below the transcript. Left in the transcript
 * they sat above the question KAI was answering, then jumped below it when
 * KAI got to them. Only while the turn is unfinished -- once it's done, or
 * stored, it's in the transcript, in order. Returns their localTurnIds.
 */
export function heldTurnIds(messages, pending, localPendingIds) {
  const ids = new Set();
  const otherRunning = (pending || []).some(
    (p) => p.status === 'running' && !localPendingIds.has(p.pending_id),
  );
  const answering = new Set(
    (messages || [])
      .filter((m) => m.role === 'assistant' && m.localTurnId && (m.isStreaming || m.awaitingTurn))
      .map((m) => m.localTurnId),
  );
  (messages || []).forEach((m) => {
    if (m.role !== 'user' || !m.localTurnId || m.message_index !== undefined) return;
    if (!answering.has(m.localTurnId)) return;
    if (m.groupQueue || otherRunning) ids.add(m.localTurnId);
  });
  return ids;
}

// Held messages (see heldTurnIds) as turns, in the order sent: the
// question's pending_id, whether KAI has started on it, and its messages.
function heldTurns(held) {
  const turns = new Map();
  (held || []).forEach((m) => {
    const turn = turns.get(m.localTurnId) || { messages: [] };
    turn.messages.push(m);
    if (m.role === 'user') {
      turn.pendingId = m.pendingId || null;
      turn.started = Boolean(m.pendingId) && !m.groupQueue;
    }
    turns.set(m.localTurnId, turn);
  });
  return [...turns.values()];
}

/**
 * ``held`` / ``renderHeld(messages)``: our own turns that wait below the
 * question KAI is answering (heldTurnIds), drawn as messages in their place
 * in the queue -- one KAI has started on first, then waiting questions,
 * ours and other people's, in the order KAI will take them.
 */
export function GroupChatQueue({
  pending, group, localPendingIds, onCancel, onSendAgain, live = null, renderRunning, held = [], renderHeld,
}) {
  const items = (pending || []).filter((p) => !localPendingIds.has(p.pending_id));
  const turns = renderHeld ? heldTurns(held) : [];
  if (!group || (items.length === 0 && turns.length === 0)) return null;
  const liveFor = (item) => (live?.text && live.pending_id === item.pending_id ? live : null);
  const asMessages = renderRunning ? items.filter((item) => item.status === 'running') : [];
  const cards = items.filter((item) => !asMessages.includes(item));
  // What follows the running answer, in queue order.
  const waiting = turns.filter((t) => t.started).map((turn) => ({ turn }));
  const placed = new Set();
  (pending || []).forEach((p) => {
    if (localPendingIds.has(p.pending_id)) {
      const turn = turns.find((t) => !t.started && t.pendingId === p.pending_id);
      if (turn) {
        waiting.push({ turn });
        placed.add(turn);
      }
    } else if (cards.includes(p)) {
      waiting.push({ card: p });
    }
  });
  turns.filter((t) => !t.started && !placed.has(t)).forEach((turn) => waiting.push({ turn }));
  // Runs of other people's cards share one box.
  const runs = [];
  waiting.forEach((entry) => {
    const last = runs[runs.length - 1];
    if (entry.card && last?.cards) last.cards.push(entry.card);
    else runs.push(entry.card ? { cards: [entry.card] } : entry);
  });
  return (
    <>
      {asMessages.map((item) => (
        <React.Fragment key={item.pending_id}>{renderRunning(item, liveFor(item))}</React.Fragment>
      ))}
      {runs.map((run) => (run.turn ? (
        <React.Fragment key={`held-${run.turn.messages[0].localTurnId}`}>{renderHeld(run.turn.messages)}</React.Fragment>
      ) : (
    <div className="gc-queue" key={`cards-${run.cards[0].pending_id}`}>
      {run.cards.map((item) => {
        const mine = item.author?.user_id === group.my_user_id;
        const name = mine ? 'You' : (item.author?.display_name || 'A member');
        const quote = pendingReplyContext(item);
        const files = pendingFiles(item);
        return (
          <div key={item.pending_id} className={`gc-queue-item gc-queue-item--${item.status}`}>
            <div className="gc-queue-head">
              <Avatar userId={item.author?.user_id} name={item.author?.display_name} size={28} />
              <span className="gc-queue-name">{name}</span>
              <span className="gc-queue-status">
                {item.status === 'running' && 'KAI is answering…'}
                {item.status === 'queued' && 'Waiting for KAI'}
                {item.status === 'not_sent' && (mine ? "Not sent: KAI didn't get to it" : 'Not sent')}
              </span>
            </div>
            {quote && (
              <div className="message-reply-caption">
                <FiCornerUpLeft size={12} />
                <div className="reply-context-info">
                  <div className="reply-context-label">{replyLabel(quote, true)}</div>
                  <div className="reply-context-snippet">{quote.text}</div>
                </div>
              </div>
            )}
            <div className="gc-queue-text">{item.text}{item.has_attachments && !files ? ' 📎' : ''}</div>
            {files && (
              <div className="gc-queue-files">📎 {files.map((f) => f.name).join(', ')}</div>
            )}
            {item.status === 'running' && liveFor(item) && (
              <div className="gc-live-answer" data-testid="gc-live-answer">
                <div className="gc-live-head">
                  <img src={kaiLogo()} alt="" aria-hidden="true" className="gc-avatar gc-avatar--photo"
                    style={{ width: 22, height: 22 }} />
                  <span>KAI</span>
                  <span className="gc-dots gc-dots--muted" aria-hidden="true"><span /><span /><span /></span>
                </div>
                <div className="gc-live-text">
                  {live.text}
                  {live.truncated && <span className="gc-live-more">…</span>}
                </div>
              </div>
            )}
            {mine && item.status !== 'running' && (
              <div className="gc-queue-actions">
                {item.status === 'not_sent' && onSendAgain && (
                  <button type="button" onClick={() => onSendAgain(item)}>Send again</button>
                )}
                <button type="button" onClick={() => onCancel(item.pending_id)}>
                  {item.status === 'not_sent' ? 'Dismiss' : 'Cancel'}
                </button>
              </div>
            )}
          </div>
        );
      })}
    </div>
      )))}
    </>
  );
}

export function NewMessagesPill({ count, onClick }) {
  if (!count) return null;
  return (
    <button type="button" className="gc-new-messages" onClick={onClick} aria-live="polite">
      {count === 1 ? '1 new message' : `${count} new messages`} <FiArrowDown size={13} />
    </button>
  );
}

// ── Who's in a chat ──────────────────────────────────────────────────────────

/**
 * A project's joined members -- ``provided`` when the caller already has
 * them, else fetched while ``isOpen``. A pending invite can't be chosen: it
 * has no user id until it's accepted.
 */
function useJoinedMembers(projectId, isOpen, provided) {
  const given = useMemo(
    () => (provided && provided.length ? provided.filter((m) => m.user_id && !m.pending) : null),
    [provided],
  );
  const [fetched, setFetched] = useState(null);
  const [error, setError] = useState('');
  const need = Boolean(isOpen && projectId && !given);
  useEffect(() => {
    if (!need) return undefined;
    let live = true;
    setFetched(null);
    setError('');
    listProjectMembers(projectId).then(
      (list) => { if (live) setFetched(list.filter((m) => m.user_id && !m.pending)); },
      (e) => { if (live) setError(friendlyError(e, "Couldn't load the project's members.")); },
    );
    return () => { live = false; };
  }, [need, projectId]);
  return { members: given || fetched || [], loading: need && fetched === null && !error, error };
}

/** A search box above the picker's people once there are more than this many to pick from. */
const PEOPLE_SEARCH_FROM = 6;

/**
 * Who's in a group chat, as a messaging app's group is chosen: "Add members"
 * from the project's people, each ticked or not, with you on top, always in
 * it. Nobody joins it by joining the project. ``rejoinedIds``: people back in
 * the project who were in the chat when they left it, said so by their name.
 * ``hideLegend``: in a dialog whose title already says what it's for -- kept
 * for screen readers. ``meHint``: why you're in this one chat, by your row
 * (none in the people dialog of a chat already going).
 * ``locked``: people already in it whom the viewer can't remove -- ticked,
 * and staying that way.
 */
function PeoplePicker({
  members, myUserId, chosen, onChange, disabled, loading, hideLegend = false, rejoinedIds = [],
  legend = 'Add members from this project', meHint = "You're starting this chat", locked = [],
}) {
  const [query, setQuery] = useState('');
  const others = members.filter((m) => m.user_id !== myUserId);
  const mine = members.find((m) => m.user_id === myUserId);
  const q = query.trim().toLowerCase();
  const shown = q
    ? others.filter((m) => `${m.display_name || ''} ${m.email || ''}`.toLowerCase().includes(q))
    : others;
  const toggle = (userId) => {
    const next = new Set(chosen);
    if (next.has(userId)) next.delete(userId);
    else next.add(userId);
    onChange(next);
  };
  const pickedCount = others.filter((m) => chosen.has(m.user_id)).length;
  const allPicked = others.length > 0 && pickedCount === others.length;
  const tickable = others.filter((m) => !locked.includes(m.user_id));
  const toggleAll = () => onChange(allPicked
    ? new Set(locked.filter((id) => chosen.has(id)))
    : new Set(others.map((m) => m.user_id)));
  return (
    <fieldset className="gc-people" disabled={disabled}>
      <legend className={`gc-people-legend${hideLegend ? ' sr-only' : ''}`}>
        {legend}
        {!loading && others.length > 0 && (
          <span className="gc-people-count">{pickedCount} of {others.length} selected</span>
        )}
        {/* On the heading line, so a long list never scrolls it away. Not
            disabled by the fieldset: a legend's own controls aren't. */}
        {!loading && tickable.length > 1 && (
          <button type="button" className="gc-people-all-btn" onClick={toggleAll} disabled={disabled}>
            {allPicked ? 'Clear all' : 'Select all'}
          </button>
        )}
      </legend>
      <div className="gc-people-card">
        {others.length > PEOPLE_SEARCH_FROM && (
          <div className="gc-people-search">
            <FiSearch size={14} aria-hidden="true" />
            <input
              type="search" value={query} onChange={(e) => setQuery(e.target.value)}
              placeholder="Search people" aria-label="Search people"
            />
          </div>
        )}
        <ul className="gc-people-list gc-people-list--plain">
          {loading && <li className="gc-people-empty">Loading the project&apos;s members…</li>}
          {!loading && (
            <li>
              <div className="gc-people-person gc-people-person--me">
                <input type="checkbox" checked disabled aria-label="You" />
                <Avatar userId={myUserId} name={mine?.display_name || 'You'} size={24} photo={mine?.profile_img} />
                <span className="gc-people-name">You</span>
                {meHint && <span className="gc-people-hint gc-people-rejoined">{meHint}</span>}
              </div>
            </li>
          )}
          {!loading && others.length === 0 && (
            <li className="gc-people-empty">
              Nobody else has joined this project yet. Invited people can be added once they accept.
            </li>
          )}
          {!loading && q && shown.length === 0 && (
            <li className="gc-people-empty">Nobody in this project matches &ldquo;{query.trim()}&rdquo;.</li>
          )}
          {shown.map((m) => {
            const label = m.display_name || m.email || 'A member';
            const rejoined = rejoinedIds.includes(m.user_id);
            const fixed = locked.includes(m.user_id);
            return (
              <li key={m.user_id}>
                <label className="gc-people-person">
                  <input
                    type="checkbox"
                    checked={fixed || chosen.has(m.user_id)}
                    onChange={() => toggle(m.user_id)}
                    disabled={fixed}
                    aria-label={label}
                  />
                  <Avatar userId={m.user_id} name={label} size={24} photo={m.profile_img} />
                  <span className="gc-people-name">{label}</span>
                  {fixed && <span className="gc-people-hint gc-people-rejoined">In this chat</span>}
                  {rejoined && <span className="gc-people-hint gc-people-rejoined">Back in the project</span>}
                </label>
              </li>
            );
          })}
        </ul>
      </div>
    </fieldset>
  );
}

/** The ids ticked who are still in the project. */
function memberIdsFor(chosen, members) {
  const joined = new Set(members.map((m) => m.user_id));
  return [...chosen].filter((id) => joined.has(id));
}

/**
 * Who's ticked in a new chat's picker: everyone else in the project until
 * the user changes it (``picked`` null), then what they picked. ``ids``:
 * what to send -- null while untouched, for everyone in the project now.
 */
function useNewChatPeople(isOpen, members, myUserId) {
  const [picked, setPicked] = useState(null);
  useEffect(() => {
    if (isOpen) setPicked(null);
  }, [isOpen]);
  const everyoneElse = new Set(members.filter((m) => m.user_id !== myUserId).map((m) => m.user_id));
  const chosen = picked || everyoneElse;
  return {
    chosen,
    setChosen: setPicked,
    ids: picked === null ? null : memberIdsFor(picked, members),
    // Nobody to make it with: ticked off, or nobody else has joined yet.
    empty: picked !== null ? memberIdsFor(picked, members).length === 0 : false,
    touched: picked !== null,
  };
}

/**
 * The people dialog's warning for a change that removes people from the
 * chat: who, and what they're left with -- a read-only copy of it as it is
 * now, if anything has been said in it (group_chat_service.keep_copies_for).
 */
export function removedText(names, keepsCopy) {
  if (!names.length) return '';
  const shown = names.length > 3 ? [...names.slice(0, 2), `${names.length - 2} others`] : names;
  const left = keepsCopy
    ? `${names.length === 1 ? "They'll" : "They'll each"} keep a read-only copy of it as it is now.`
    : "They won't be able to open it any more.";
  return `${joinNames(shown)} will be removed from this chat. ${left} Their messages stay in it.`;
}

/** Start a new group chat with the people chosen -- everyone else in the project, to begin with. */
export function GroupChatCreateModal({ isOpen, project, members: provided, myUserId, onClose, onCreated }) {
  const projectId = project?.project_id;
  const { members, loading, error: membersError } = useJoinedMembers(projectId, isOpen, provided);
  const people = useNewChatPeople(isOpen, members, myUserId);
  const [name, setName] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (isOpen) {
      setName('');
      setError('');
    }
  }, [isOpen]);

  if (!isOpen || !project) return null;

  const { empty } = people;
  const handleConfirm = async () => {
    setBusy(true);
    setError('');
    try {
      const conversation = await createGroupChat(projectId, {
        name: name.trim(), memberIds: people.ids,
      });
      onCreated?.(conversation);
    } catch (e) {
      setError(friendlyError(e, "Couldn't start the group chat."));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="gc-modal-overlay" onClick={busy ? undefined : onClose} role="presentation">
      <div className="gc-modal" role="dialog" aria-modal="true" aria-labelledby="gc-create-title"
        onClick={(e) => e.stopPropagation()}>
        <div className="gc-modal-header">
          <h2 id="gc-create-title">New group chat in &ldquo;{project.name}&rdquo;</h2>
          <button type="button" className="gc-modal-close" onClick={onClose} disabled={busy} aria-label="Close">
            <FiX size={18} />
          </button>
        </div>
        <div className="gc-modal-body">
          <label className="gc-name-field">
            <span className="gc-name-label">Name <span className="gc-name-optional">(optional)</span></span>
            {/* Focused on opening: naming it is the first thing to do here. */}
            {/* eslint-disable-next-line jsx-a11y/no-autofocus */}
            <input
              type="text" className="gc-name-input" value={name} maxLength={200} disabled={busy} autoFocus
              onChange={(e) => setName(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter' && !busy && !empty) handleConfirm(); }}
              placeholder="e.g. Facade options" aria-describedby="gc-create-name-hint"
            />
            <span id="gc-create-name-hint" className="gc-name-hint">
              Leave it blank and it&apos;s named from the first question.
            </span>
          </label>
          <PeoplePicker
            members={members} myUserId={myUserId} loading={loading}
            chosen={people.chosen} onChange={people.setChosen} disabled={busy}
          />
          <p className="gc-people-note">
            Members can read the whole chat and ask KAI here, using the project&apos;s knowledge base.
            You can add more people later, including anyone who joins the project after today.
          </p>
          {(error || membersError) && <div className="gc-modal-error" role="alert">{error || membersError}</div>}
        </div>
        <div className="gc-modal-actions">
          <button type="button" className="gc-btn" onClick={onClose} disabled={busy}>Cancel</button>
          <button type="button" className="gc-btn gc-btn--primary" onClick={handleConfirm}
            disabled={busy || empty || (people.touched && loading)}
            title={empty ? 'Choose at least one person' : undefined}>
            {busy ? 'Starting…' : 'Start group chat'}
          </button>
        </div>
      </div>
    </div>
  );
}

/**
 * Who's in a group chat. Whoever manages it (its starter, or once they've
 * left whoever has been in it longest) adds and removes people, whatever
 * their role in the project; anyone else in it with Edit access to the
 * project only adds; a View member sees the list, and who manages it.
 * ``onLeave``: anyone in it can leave it from here.
 */
export function GroupChatPeopleModal({ isOpen, conversationId, projectId, group, people = null, onClose, onSaved,
  onInvite, onLeave, hasMessages = false }) {
  const canRemove = people ? people.canManagePeople : Boolean(group?.can_manage_people);
  const canChange = canRemove || (people ? people.canAddPeople : Boolean(group?.can_add_people));
  // Inviting to the project is for its owner and Edit members, whoever manages the chat.
  const canInvite = Boolean(onInvite) && ['owner', 'edit'].includes(group?.my_role);
  const { members, loading, error: membersError } = useJoinedMembers(projectId, isOpen && canChange, null);
  const current = people?.members || group?.members || [];
  const manager = (people ? people.manager : group?.manager) || null;
  const rejoined = (people ? people.rejoined : group?.rejoined) || [];
  const rejoinedIds = rejoined.map((p) => p.user_id);
  const me = group?.my_user_id;
  const [chosen, setChosen] = useState(new Set());
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!isOpen) return;
    // Everyone in it now ticked.
    setChosen(new Set(current.map((m) => m.user_id).filter((id) => id !== me)));
    setError('');
  // Only on opening: the poll refreshing ``current`` mustn't undo ticks being made.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  if (!isOpen || !group) return null;

  // Adding only: everyone in it now stays ticked.
  const locked = canRemove ? [] : current.map((m) => m.user_id).filter((id) => id !== me);
  const added = canRemove ? [] : memberIdsFor(chosen, members).filter((id) => !locked.includes(id));
  const empty = canRemove && memberIdsFor(chosen, members).length === 0;
  // Who the change removes: everyone in it now but you and the people picked.
  // Not until the project's members are in, or everyone would look removed.
  const after = loading ? null : memberIdsFor(chosen, members);
  const removed = after === null ? [] : current.filter(
    (m) => m.user_id !== me && !after.includes(m.user_id),
  );
  const handleSave = async () => {
    setBusy(true);
    setError('');
    try {
      const result = await setGroupChatMembers(conversationId, memberIdsFor(chosen, members));
      onSaved?.(result.group);
    } catch (e) {
      setError(friendlyError(e, "Couldn't change who's in this group chat."));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="gc-modal-overlay" onClick={busy ? undefined : onClose} role="presentation">
      <div className="gc-modal" role="dialog" aria-modal="true" aria-labelledby="gc-people-title"
        onClick={(e) => e.stopPropagation()}>
        <div className="gc-modal-header">
          <h2 id="gc-people-title">Who&apos;s in this chat</h2>
          <button type="button" className="gc-modal-close" onClick={onClose} disabled={busy} aria-label="Close">
            <FiX size={18} />
          </button>
        </div>
        <div className="gc-modal-body">
          {canChange ? (
            <>
              {!canRemove && (
                <p className="gc-people-note">
                  You can add people from the project.
                  {` Only ${manager && manager.user_id !== me ? manager.display_name : 'whoever manages it'} can remove anyone.`}
                </p>
              )}
              <PeoplePicker
                members={members} myUserId={me} loading={loading} rejoinedIds={rejoinedIds}
                chosen={chosen} onChange={setChosen} disabled={busy} hideLegend legend="Who's in it"
                meHint={null} locked={locked}
              />
              {/* Why Save is off, before who it would remove: it can't. */}
              {empty && !loading ? (
                <p className="gc-people-note gc-people-note--warn" role="status">
                  Choose at least one other person. A group chat needs someone in it besides you.
                </p>
              ) : removed.length > 0 && (
                <p className="gc-people-note gc-people-note--warn" role="status">
                  {removedText(removed.map((m) => m.display_name || 'A member'), hasMessages)}
                </p>
              )}
              {canInvite && (
                <div className="gc-invite-callout">
                  <span className="gc-invite-callout-icon" aria-hidden="true"><FiUserPlus size={16} /></span>
                  <div className="gc-invite-callout-text">
                    <strong>Someone not in the project yet?</strong>
                    <span>Invite them to the project. Once they&apos;ve accepted, you can add them here.</span>
                  </div>
                  <button type="button" className="gc-btn gc-btn--small" onClick={onInvite}>
                    Invite to project
                  </button>
                </div>
              )}
            </>
          ) : (
            <>
              <p className="gc-people-note">
                Only these people can read this chat. Nobody else in the project can see it.
                {manager && manager.user_id !== me && ` ${manager.display_name} manages who's in it.`}
              </p>
              <ul className="gc-people-list">
                {current.map((m) => (
                  <li key={m.user_id} className="gc-people-person">
                    <Avatar userId={m.user_id} name={m.display_name} size={24} />
                    <span className="gc-people-name">{m.user_id === me ? `${m.display_name} (you)` : m.display_name}</span>
                  </li>
                ))}
              </ul>
              {rejoined.length > 0 && (
                <>
                  <p className="gc-people-note">
                    Back in the project, but not in this chat yet. Whoever manages it, or anyone in it with
                    Edit access, can add them back.
                  </p>
                  <ul className="gc-people-list">
                    {rejoined.map((p) => (
                      <li key={p.user_id} className="gc-people-person">
                        <Avatar userId={p.user_id} name={p.display_name} size={24} />
                        <span className="gc-people-name">{p.display_name}</span>
                      </li>
                    ))}
                  </ul>
                </>
              )}
            </>
          )}
          {(error || membersError) && <div className="gc-modal-error" role="alert">{error || membersError}</div>}
        </div>
        <div className="gc-modal-actions">
          {onLeave && group.can_leave && (
            <button type="button" className="gc-btn gc-btn--leave" onClick={onLeave} disabled={busy}>
              <FiLogOut size={14} /> Leave chat
            </button>
          )}
          {canChange ? (
            <>
              <button type="button" className="gc-btn" onClick={onClose} disabled={busy}>Cancel</button>
              <button type="button" className="gc-btn gc-btn--primary" onClick={handleSave}
                disabled={busy || empty || loading || (!canRemove && added.length === 0)}
                title={empty ? 'Choose at least one other person' : undefined}>
                {canRemove ? (busy ? 'Saving…' : 'Save') : (busy ? 'Adding…' : 'Add')}
              </button>
            </>
          ) : (
            <button type="button" className="gc-btn" onClick={onClose}>Close</button>
          )}
        </div>
      </div>
    </div>
  );
}

// ── Project panel ────────────────────────────────────────────────────────────

/** Anyone besides the creator in it, joined or still invited. */
export function projectIsShared(project) {
  return Boolean(project?.shared || project?.ownerless || project?.shared_with > 0 || project?.pending_invites > 0);
}

const LIST_REFRESH_MS = 20000;

/** The chats' last activity, as one value to compare: whether anything new happened. */
function activityStamp(list) {
  return (list || []).map((c) => `${c.conversation_id}:${c.last_activity_at || ''}`).join('|');
}

/** A row's unread count as shown: past 99, "99+". */
function unreadCountLabel(count) {
  return count > 99 ? '99+' : String(count);
}

/**
 * The project panel's "Group chats" section, above the caller's own
 * conversations: each chat with who's posted, its last activity, how many
 * messages are unread (or a dot, for other news) and whether KAI is
 * answering in it right now.
 */
export function GroupChatsSection({
  project, activeConversationId, onOpenConversation, onInviteRequested, refreshKey = 0, notice = null,
  // Bumped each time a reply finishes, so the list's "Priya · now" keeps up.
  activityKey = 0,
  // The project's joined members and the viewer's own id, for choosing
  // who's in a new chat.
  members = null, myUserId = null,
  // A chat deleted (or left) from here, so a chat screen showing it can let it go.
  onChatDeleted,
  // The caller's read-only copies of this project's chats they're no longer
  // in (their own conversations, from the project panel's list), shown under
  // the live ones; and a chat just left from here, so that list picks up the
  // copy it left behind.
  leftChats = [],
  onChatLeft,
  // The project's group chats have just been marked seen, so a copy of the
  // project held elsewhere (App's list, its Projects badge) can drop its dot.
  onGroupChatsSeen,
  // How many chats the project has, each time the list loads or changes
  // (the project panel's Conversations tab counts them).
  onChatsCounted,
  // The panel's "Search conversations" text, lower-cased: chats shown are
  // those whose name or focus mode tag has it (as for its own conversations).
  searchQuery = '',
}) {
  const projectId = project?.project_id;
  const shared = projectIsShared(project);
  const [chats, setChats] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [creating, setCreating] = useState(false);
  // { id, name } while a chat's name is being edited in place.
  const [renaming, setRenaming] = useState(null);
  const [savingName, setSavingName] = useState(false);
  const [leaving, setLeaving] = useState(null);
  const [deletingCopy, setDeletingCopy] = useState(null);
  // The heading folds the section away, as the sidebar's "Folders" one does.
  const [collapsed, setCollapsed] = useState(false);
  const retryRef = useRef(null);
  const onSeenRef = useRef(onGroupChatsSeen);
  onSeenRef.current = onGroupChatsSeen;
  const onCountedRef = useRef(onChatsCounted);
  onCountedRef.current = onChatsCounted;
  // The project the list was last loaded for: until this one's has, the
  // list (empty, or another project's) isn't counted.
  const loadedForRef = useRef(null);
  // The chats' last activity when the project's dot was last cleared: newer
  // activity reaching this list while it's on screen has been seen too.
  const seenActivityRef = useRef(null);
  // The chat open before this one: read while it was, so its count (from a
  // list loaded then) mustn't show once it's left.
  const previousActiveRef = useRef(activeConversationId);
  useEffect(() => {
    const left = previousActiveRef.current;
    previousActiveRef.current = activeConversationId;
    if (!left || left === activeConversationId) return;
    setChats((prev) => prev.map((c) => (
      c.conversation_id === left && (c.unread || c.unread_count) ? { ...c, unread: false, unread_count: 0 } : c
    )));
  }, [activeConversationId]);

  const markSeen = useCallback((list) => {
    seenActivityRef.current = activityStamp(list);
    markProjectGroupChatsSeen(projectId)
      .then(() => onSeenRef.current?.(projectId))
      .catch(() => {});
  }, [projectId]);

  const load = useCallback(async ({ quiet = false, seen = false } = {}) => {
    if (!projectId) return;
    if (!quiet) setLoading(true);
    try {
      const list = await listProjectGroupChats(projectId);
      loadedForRef.current = projectId;
      setChats(list);
      setError('');
      // Opening the list clears the project's dot; so does new activity
      // turning up in it while it's on screen -- it's been seen here, so it
      // shouldn't come back as a dot on the project.
      const visible = typeof document === 'undefined' || document.visibilityState === 'visible';
      if (seen || (visible && activityStamp(list) !== seenActivityRef.current)) markSeen(list);
    } catch (e) {
      if (!quiet) {
        // Tried again shortly, rather than leaving it until the next
        // refresh -- a success then clears this.
        setError(`${friendlyError(e, "Couldn't load group chats.")} Trying again…`);
        clearTimeout(retryRef.current);
        retryRef.current = setTimeout(() => load({ quiet: true }), 5000);
      }
    } finally {
      if (!quiet) setLoading(false);
    }
  }, [projectId, markSeen]);

  useEffect(() => () => clearTimeout(retryRef.current), []);

  useEffect(() => {
    if (loadedForRef.current === projectId) onCountedRef.current?.(chats.length);
  }, [chats, projectId]);

  // Another project's section starts open, as its folder headings do.
  useEffect(() => setCollapsed(false), [projectId]);

  useEffect(() => {
    load({ seen: true });
  }, [load, projectId, refreshKey]);

  useEffect(() => {
    if (activityKey) load({ quiet: true });
  }, [activityKey, load]);

  useEffect(() => {
    if (!projectId) return undefined;
    const timer = setInterval(() => {
      if (document.visibilityState === 'visible') load({ quiet: true });
    }, LIST_REFRESH_MS);
    return () => clearInterval(timer);
  }, [projectId, load]);

  if (!projectId) return null;

  const matches = (chat) => !searchQuery
    || chat.name.toLowerCase().includes(searchQuery)
    || focusModeTagMatches(chat.focus_mode, searchQuery);
  const shownChats = chats.filter(matches);
  const shownLeftChats = leftChats.filter(matches);

  // Opening a chat reads it (the server marks it as it loads): its count goes now.
  const openChat = (chat) => {
    if (chat.unread || chat.unread_count) {
      setChats((prev) => prev.map((c) => (
        c.conversation_id === chat.conversation_id ? { ...c, unread: false, unread_count: 0 } : c
      )));
    }
    onOpenConversation?.({ ...chat, is_group: true });
  };

  const handleCreated = async (conversation) => {
    setCreating(false);
    setCollapsed(false);
    setError('');
    await load({ quiet: true });
    onOpenConversation?.(conversation);
  };

  const handleSaveName = async (chat) => {
    const name = (renaming?.name || '').trim();
    if (!name || savingName) return;
    if (name === chat.name) {
      setRenaming(null);
      return;
    }
    setSavingName(true);
    try {
      await renameGroupChat(chat.conversation_id, name);
      setChats((prev) => prev.map((c) => (c.conversation_id === chat.conversation_id ? { ...c, name } : c)));
      setRenaming(null);
      setError('');
    } catch (e) {
      setError(friendlyError(e, "Couldn't rename the group chat."));
    } finally {
      setSavingName(false);
    }
  };

  // Deleted or left: either way it's gone from this list.
  const handleDeleted = (conversationId) => {
    setLeaving(null);
    setChats((prev) => prev.filter((c) => c.conversation_id !== conversationId));
    onChatDeleted?.(conversationId);
  };

  const handleLeft = (conversationId) => {
    handleDeleted(conversationId);
    onChatLeft?.(conversationId);
  };

  const handleCopyDeleted = (conversationId) => {
    setDeletingCopy(null);
    onChatDeleted?.(conversationId);
  };

  return (
    <MemberPhotosProvider projectId={projectId}>
    <div className="gc-section">
      <div className={`gc-section-head${collapsed ? ' collapsed' : ''}`} onClick={() => setCollapsed((v) => !v)}>
        <button
          type="button"
          className="gc-section-title"
          aria-expanded={!collapsed}
          onClick={(e) => {
            e.stopPropagation();
            setCollapsed((v) => !v);
          }}
        >
          Group chats
          {collapsed && chats.length + leftChats.length > 0 && (
            <span className="folders-toolbar-count">{chats.length + leftChats.length}</span>
          )}
        </button>
        {shared && (
          <button type="button" className="gc-section-new"
            onClick={(e) => { e.stopPropagation(); setCreating(true); }} disabled={creating}
            title="Start a group chat with people in this project">
            <FiPlus size={13} /> New group chat
          </button>
        )}
      </div>
      {notice && <div className="gc-section-notice" role="status">{notice}</div>}
      {error && <div className="project-panel-error">{error}</div>}
      {!collapsed && (
      <>
      {!shared && (
        <div className="gc-section-empty">
          {chats.length > 0
            ? "You're the only one in this project now. You can carry on in these chats, or invite people."
            : 'Group chats are shared with the people in this project. Invite someone to start one.'}
          {onInviteRequested && (
            <button type="button" className="gc-section-link" onClick={onInviteRequested}>Invite people</button>
          )}
        </div>
      )}
      {shared && !loading && chats.length === 0 && (
        <div className="gc-section-empty">
          Work through something together: start a group chat with the people in this project you
          choose.
        </div>
      )}
      {searchQuery && chats.length + leftChats.length > 0 && shownChats.length + shownLeftChats.length === 0 && (
        <div className="gc-section-empty">No group chats match your search.</div>
      )}
      {shownChats.length + shownLeftChats.length > 0 && (
      <div className="gc-rows">
      {shownChats.map((chat) => {
        const lastBy = chat.last_activity_by?.display_name;
        const active = chat.conversation_id === activeConversationId;
        if (renaming?.id === chat.conversation_id) {
          return (
            <form
              key={chat.conversation_id}
              className={`gc-row gc-row--editing${active ? ' active' : ''}`}
              onSubmit={(e) => { e.preventDefault(); handleSaveName(chat); }}
            >
              <input
                type="text" className="gc-name-input" value={renaming.name} maxLength={200} autoFocus
                aria-label="Group chat name" disabled={savingName}
                onChange={(e) => setRenaming({ id: chat.conversation_id, name: e.target.value })}
                onKeyDown={(e) => { if (e.key === 'Escape') setRenaming(null); }}
              />
              <button type="submit" className="gc-row-action" disabled={savingName || !renaming.name.trim()}
                title="Save" aria-label="Save name">
                <FiCheck size={14} />
              </button>
              <button type="button" className="gc-row-action" onClick={() => setRenaming(null)} disabled={savingName}
                title="Cancel" aria-label="Cancel renaming">
                <FiX size={14} />
              </button>
            </form>
          );
        }
        const limited = chat.everyone === false;
        const memberNames = (chat.members || []).map((m) => m.display_name).filter(Boolean).join('; ');
        // How many of rename, delete and leave it shows: the room kept for them on hover.
        const actions = [chat.can_manage, chat.can_leave].filter(Boolean).length;
        // A <div>, not a <button>: it holds its own rename, delete and leave
        // buttons, and a button can't contain another.
        return (
          <div key={chat.conversation_id} className={`gc-row${active ? ' active' : ''}`}
            style={{ '--gc-actions': actions }}>
            <button
              type="button"
              className="gc-row-open"
              onClick={() => openChat(chat)}
              title={limited ? `${chat.name}\nOnly for ${memberNames}` : chat.name}
            >
              <span className="gc-row-main">
                <span className="gc-row-name">
                  {/* As a folder row leads with its folder icon. */}
                  <FiUsers size={14} className="gc-row-icon" aria-hidden="true" />
                  {/* Never on the one open: it's being read as it changes. A
                      count says how many messages; this dot is for other news. */}
                  {chat.unread && !chat.unread_count && !active && (
                    <span className="gc-unread-dot" aria-label="Unread" />
                  )}
                  <FocusModeTag focusMode={chat.focus_mode} />
                  <span className="gc-row-title">{chat.name}</span>
                </span>
                <span className="gc-row-meta">
                  {chat.turn ? 'KAI is answering…' : [lastBy, relativeTime(chat.last_activity_at)].filter(Boolean).join(' · ')}
                </span>
              </span>
              {chat.unread_count > 0 && !active && (
                <span
                  className="gc-unread-count"
                  aria-label={`${chat.unread_count} unread message${chat.unread_count === 1 ? '' : 's'}`}
                >
                  {unreadCountLabel(chat.unread_count)}
                </span>
              )}
            </button>
            {(chat.can_manage || chat.can_leave) && (
              <span className="gc-row-actions">
                {chat.can_manage && (
                  <button
                    type="button" className="gc-row-action"
                    onClick={() => setRenaming({ id: chat.conversation_id, name: chat.name })}
                    title="Rename" aria-label={`Rename group chat "${chat.name}"`}
                  >
                    <FiEdit2 size={13} />
                  </button>
                )}
                {chat.can_leave && (
                  <button
                    type="button" className="gc-row-action"
                    onClick={() => setLeaving(chat)}
                    title="Leave this chat" aria-label={`Leave group chat "${chat.name}"`}
                  >
                    <FiLogOut size={13} />
                  </button>
                )}
              </span>
            )}
          </div>
        );
      })}
      {shownLeftChats.map((chat) => {
        const active = chat.conversation_id === activeConversationId;
        return (
          <div key={chat.conversation_id} className={`gc-row gc-row--left${active ? ' active' : ''}`}
            style={{ '--gc-actions': 1 }}>
            <button
              type="button"
              className="gc-row-open"
              onClick={() => onOpenConversation?.(chat)}
              title={`${chat.name}\n${leftGroupText(chat.left_group)} Read only.`}
            >
              <span className="gc-row-main">
                <span className="gc-row-name">
                  <FiUsers size={14} className="gc-row-icon" aria-hidden="true" />
                  <FocusModeTag focusMode={chat.focus_mode} />
                  <span className="gc-row-title">{chat.name}</span>
                </span>
                <span className="gc-row-meta">
                  {[leftGroupLabel(chat.left_group), relativeTime(chat.left_group?.at)].filter(Boolean).join(' · ')}
                </span>
              </span>
            </button>
            <span className="gc-row-actions">
              <button
                type="button" className="gc-row-action gc-row-action--danger"
                onClick={() => setDeletingCopy(chat)}
                title="Delete your copy" aria-label={`Delete your copy of "${chat.name}"`}
              >
                <FiTrash2 size={13} />
              </button>
            </span>
          </div>
        );
      })}
      </div>
      )}
      </>
      )}
      <GroupChatCreateModal
        isOpen={creating}
        project={project}
        members={members}
        myUserId={myUserId}
        onClose={() => setCreating(false)}
        onCreated={handleCreated}
      />
      <GroupChatDeleteModal chat={deletingCopy} onClose={() => setDeletingCopy(null)}
        onDeleted={handleCopyDeleted} />
      <GroupChatLeaveModal chat={leaving} alone={onlyOneIn(leaving, myUserId)}
        manages={Boolean(leaving?.can_manage_people)} empty={leaving?.has_messages === false}
        onClose={() => setLeaving(null)} onLeft={handleLeft} />
    </div>
    </MemberPhotosProvider>
  );
}

/** Whether ``chat`` (a list row: its members) has nobody in it but ``myUserId``. */
function onlyOneIn(chat, myUserId) {
  const members = chat?.members || [];
  return members.length > 0 && members.every((m) => m.user_id === myUserId);
}

/**
 * Asked of the person a group chat was copied from -- a conversation they
 * shared, which someone imported and made a group chat with them in it --
 * when they open the chat (group.ask_original): keep their own as well, or
 * have just the group chat? ``chat``: {conversation_id, name}, the group
 * chat's. ``onDone(removedId)`` once answered: their own conversation's id
 * if they removed it, else null.
 */
export function GroupChatOriginalCopyModal({ chat, onClose, onDone }) {
  const [details, setDetails] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const onDoneRef = useRef(onDone);
  onDoneRef.current = onDone;
  const chatId = chat?.conversation_id;

  useEffect(() => {
    setDetails(null);
    setError('');
    if (!chatId) return undefined;
    let cancelled = false;
    getGroupChatOriginalCopy(chatId)
      .then((d) => { if (!cancelled) setDetails(d); })
      .catch((e) => {
        if (cancelled) return;
        // Nothing to ask any more (kept or deleted elsewhere).
        if (e?.status === 404) onDoneRef.current?.(null);
        else setError(friendlyError(e, "Couldn't check your own copy of this conversation."));
      });
    return () => { cancelled = true; };
  }, [chatId]);

  if (!chat) return null;

  const answer = async (keep) => {
    setBusy(true);
    setError('');
    try {
      if (keep) {
        await keepGroupChatOriginalCopy(chatId);
        onDone?.(null);
      } else {
        await deleteConversation(details.conversation_id);
        onDone?.(details.conversation_id);
      }
    } catch (e) {
      setError(friendlyError(e, keep ? "Couldn't keep your copy." : "Couldn't remove your copy."));
    } finally {
      setBusy(false);
    }
  };

  const name = details?.name || chat.name;
  return (
    <div className="gc-modal-overlay" onClick={busy ? undefined : onClose} role="presentation">
      <div className="gc-modal" role="dialog" aria-modal="true" aria-labelledby="gc-original-title"
        aria-describedby="gc-original-body" onClick={(e) => e.stopPropagation()}>
        <div className="gc-modal-header">
          <h2 id="gc-original-title">Your conversation is now a group chat</h2>
          <button type="button" className="gc-modal-close" onClick={onClose} disabled={busy} aria-label="Close">
            <FiX size={18} />
          </button>
        </div>
        <div className="gc-modal-body" id="gc-original-body">
          <p className="gc-people-note">
            {details?.made_by || 'Someone'} made a group chat from &ldquo;{name}&rdquo;, the conversation you
            shared with them, and you&apos;re in it. Do you want to keep your own copy as well, or have just
            the group chat?
          </p>
          {details?.has_newer && (
            <p className="gc-people-note gc-people-note--warn" role="status">
              Your copy has messages from after it was shared that the group chat doesn&apos;t. Removing it
              deletes them.
            </p>
          )}
          {error && <div className="gc-modal-error" role="alert">{error}</div>}
        </div>
        <div className="gc-modal-actions">
          <button type="button" className="gc-btn" onClick={onClose} disabled={busy}>Not now</button>
          <button type="button" className="gc-btn" onClick={() => answer(false)} disabled={busy || !details}>
            Just the group chat
          </button>
          <button type="button" className="gc-btn gc-btn--primary" onClick={() => answer(true)}
            disabled={busy || !details}>
            Keep both
          </button>
        </div>
      </div>
    </div>
  );
}

/**
 * "Switch this chat to <mode>?" -- a group chat's focus mode is everyone's:
 * it changes how KAI answers every question in it, and a note in it says who
 * switched it. Anyone in the chat can. ``change``: {chatName, fromName,
 * toName}, or null when closed. ``onConfirm``: does the switch; its error
 * (KAI is answering, say) is shown as is.
 */
export function GroupChatFocusModeConfirm({ change, onClose, onConfirm }) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    setError('');
  }, [change]);

  if (!change) return null;

  const handleConfirm = async () => {
    setBusy(true);
    setError('');
    try {
      await onConfirm();
    } catch (e) {
      setError(friendlyError(e, "Couldn't change this chat's focus mode."));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="gc-modal-overlay" onClick={busy ? undefined : onClose} role="presentation">
      <div className="gc-modal" role="alertdialog" aria-modal="true" aria-labelledby="gc-focus-title"
        aria-describedby="gc-focus-body" onClick={(e) => e.stopPropagation()}>
        <div className="gc-modal-header">
          <h2 id="gc-focus-title">Switch this chat to {change.toName}?</h2>
          <button type="button" className="gc-modal-close" onClick={onClose} disabled={busy} aria-label="Close">
            <FiX size={18} />
          </button>
        </div>
        <div className="gc-modal-body" id="gc-focus-body">
          <p className="gc-people-note">
            This changes it for everyone in &ldquo;{change.chatName}&rdquo;, not just you: KAI answers
            everyone&apos;s questions in {change.toName}
            {change.fromName ? ` instead of ${change.fromName}` : ''}. Everyone in the chat sees that you
            switched it.
          </p>
          {error && <div className="gc-modal-error" role="alert">{error}</div>}
        </div>
        <div className="gc-modal-actions">
          <button type="button" className="gc-btn" onClick={onClose} disabled={busy}>Cancel</button>
          <button type="button" className="gc-btn gc-btn--primary" onClick={handleConfirm} disabled={busy}>
            {busy ? 'Switching…' : 'Switch for everyone'}
          </button>
        </div>
      </div>
    </div>
  );
}

/**
 * "Delete your copy?" -- the read-only copy kept on leaving a group chat. In
 * a chat you're still in there's no Delete, only Leave (WhatsApp's way):
 * leaving without a copy is its "delete for me".
 */
export function GroupChatDeleteModal({ chat, onClose, onDeleted }) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    setError('');
  }, [chat]);

  if (!chat) return null;

  const handleDelete = async () => {
    setBusy(true);
    setError('');
    try {
      await deleteConversation(chat.conversation_id);
      onDeleted?.(chat.conversation_id);
    } catch (e) {
      // Gone already: what was asked for is done.
      if (e?.status === 404) onDeleted?.(chat.conversation_id);
      else setError(friendlyError(e, "Couldn't delete your copy."));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="gc-modal-overlay" onClick={busy ? undefined : onClose} role="presentation">
      <div className="gc-modal" role="alertdialog" aria-modal="true" aria-labelledby="gc-delete-title"
        aria-describedby="gc-delete-body" onClick={(e) => e.stopPropagation()}>
        <div className="gc-modal-header">
          <h2 id="gc-delete-title">Delete your copy of &ldquo;{chat.name}&rdquo;?</h2>
          <button type="button" className="gc-modal-close" onClick={onClose} disabled={busy} aria-label="Close">
            <FiX size={18} />
          </button>
        </div>
        <div className="gc-modal-body" id="gc-delete-body">
          <p className="gc-people-note">
            Your read-only copy is deleted. The group chat itself carries on for the people still in it.
            This can&apos;t be undone.
          </p>
          {error && <div className="gc-modal-error" role="alert">{error}</div>}
        </div>
        <div className="gc-modal-actions">
          <button type="button" className="gc-btn" onClick={onClose} disabled={busy}>Cancel</button>
          <button type="button" className="gc-btn gc-btn--danger" onClick={handleDelete} disabled={busy}>
            {busy ? 'Deleting…' : 'Delete my copy'}
          </button>
        </div>
      </div>
    </div>
  );
}

/**
 * "Leave this group chat?" -- anyone in it can. They keep a read-only copy of
 * it as it stands unless they untick it (leaving without one is the "delete
 * for me"), and lose that copy again if they're added back. ``alone``:
 * they're the only one in it, so the chat itself goes. ``manages``: they add
 * and remove people in it, which passes to whoever has been in it longest.
 * ``empty``: nothing's been said in it yet, so there's no copy to keep.
 * Refused while KAI is answering in it, and that reason is shown as is.
 */
export function GroupChatLeaveModal({ chat, alone = false, manages = false, empty = false, onClose, onLeft }) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [keepCopy, setKeepCopy] = useState(true);

  useEffect(() => {
    setError('');
    setKeepCopy(true);
  }, [chat]);

  if (!chat) return null;

  const handleLeave = async () => {
    setBusy(true);
    setError('');
    try {
      await (keepCopy || empty ? leaveGroupChat : deleteGroupChat)(chat.conversation_id);
      onLeft?.(chat.conversation_id);
    } catch (e) {
      // Gone already, or no longer theirs: either way they're out of it.
      if (e?.status === 404) onLeft?.(chat.conversation_id);
      else setError(friendlyError(e, "Couldn't leave the group chat."));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="gc-modal-overlay" onClick={busy ? undefined : onClose} role="presentation">
      <div className="gc-modal" role="alertdialog" aria-modal="true" aria-labelledby="gc-leave-title"
        aria-describedby="gc-leave-body" onClick={(e) => e.stopPropagation()}>
        <div className="gc-modal-header">
          <h2 id="gc-leave-title">Leave &ldquo;{chat.name}&rdquo;?</h2>
          <button type="button" className="gc-modal-close" onClick={onClose} disabled={busy} aria-label="Close">
            <FiX size={18} />
          </button>
        </div>
        <div className="gc-modal-body" id="gc-leave-body">
          <p className="gc-people-note">
            {alone
              ? "You're the only one in it, so leaving deletes the chat itself."
              : "You won't see anything said after you leave or be able to ask KAI in it, unless you're added "
                + 'back. Your messages stay in it, and everyone still in the chat sees that you left.'}
            {!alone && manages && " Whoever has been in it longest will add and remove people in it after you."}
          </p>
          {empty ? (
            <p className="gc-people-note">Nothing has been asked in it yet, so there&apos;s no copy to keep.</p>
          ) : (
            <label className="gc-keep-copy">
              <input type="checkbox" checked={keepCopy} onChange={(e) => setKeepCopy(e.target.checked)}
                disabled={busy} />
              <span>
                Keep a read-only copy
                <span className="gc-people-hint">
                  {keepCopy ? 'It stays in your Group chats, as it is now.' : 'It goes from your list.'}
                </span>
              </span>
            </label>
          )}
          {error && <div className="gc-modal-error" role="alert">{error}</div>}
        </div>
        <div className="gc-modal-actions">
          <button type="button" className="gc-btn" onClick={onClose} disabled={busy}>Cancel</button>
          <button type="button" className="gc-btn gc-btn--danger" onClick={handleLeave} disabled={busy}>
            {busy ? 'Leaving…' : 'Leave chat'}
          </button>
        </div>
      </div>
    </div>
  );
}

/**
 * Making one of your own project conversations a group chat: the form -- who
 * it's for, and what changes, since it can't be undone -- and its confirm,
 * for GroupChatPromoteModal and Share's "Make group chat" tab to lay out in
 * their own frames. ``nobodyElse``: nobody else has joined the project yet,
 * so there's nobody to make it a group chat with.
 */
export function useGroupChatPromote({ isOpen, conversation, project, members, myUserId, onPromoted }) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const { members: joined, loading, error: membersError } = useJoinedMembers(project?.project_id, isOpen, members);
  const people = useNewChatPeople(isOpen, joined, myUserId);

  useEffect(() => {
    if (isOpen) setError('');
  }, [isOpen]);

  const memberIds = people.ids;
  const { empty } = people;
  // A copy of someone's shared conversation: they keep their own, and if
  // they're in the chat they're asked whether they still want it.
  const origin = conversation?.imported_from;
  const originIn = Boolean(origin?.user_id) && origin.user_id !== myUserId && joined.some(
    (m) => m.user_id === origin.user_id && (memberIds === null || memberIds.includes(m.user_id)),
  );
  const nobodyElse = !loading && !membersError && !joined.some((m) => m.user_id !== myUserId);
  const handleConfirm = async () => {
    setBusy(true);
    setError('');
    try {
      // Nobody is emailed: they find it in the project's Group chats.
      const result = await promoteConversationToGroup(project.project_id, conversation.conversation_id, {
        memberIds,
      });
      onPromoted?.(result);
    } catch (e) {
      setError(friendlyError(e, "Couldn't make this a group chat."));
    } finally {
      setBusy(false);
    }
  };

  const form = (
    <>
      <PeoplePicker
        members={joined} myUserId={myUserId} loading={loading}
        chosen={people.chosen} onChange={people.setChosen} disabled={busy}
      />
      <ul className="gc-facts">
        <li><FiEye size={14} aria-hidden="true" />Everyone in it sees the whole history and every answer.</li>
        <li><FiLock size={14} aria-hidden="true" />Personal memory and rules don&apos;t apply here.</li>
        <li><FiAlertCircle size={14} aria-hidden="true" />This can&apos;t be undone.</li>
        {originIn && (
          <li>
            <FiUsers size={14} aria-hidden="true" />
            {origin.display_name || 'The person who shared it'} still has their own copy. They&apos;ll be
            asked whether to keep it.
          </li>
        )}
      </ul>
      {(error || membersError) && <div className="gc-modal-error" role="alert">{error || membersError}</div>}
    </>
  );
  return {
    form,
    confirm: handleConfirm,
    busy,
    empty,
    nobodyElse,
    canConfirm: !busy && !empty && !(people.touched && loading),
    confirmLabel: busy ? 'Making it a group chat…' : 'Make group chat',
  };
}

/** "Make this a group chat?" on its own, from a conversation's row in the project panel. */
export function GroupChatPromoteModal({ isOpen, conversation, project, members, myUserId, onClose, onPromoted }) {
  const promote = useGroupChatPromote({ isOpen, conversation, project, members, myUserId, onPromoted });
  if (!isOpen || !conversation) return null;
  return (
    <div className="gc-modal-overlay" onClick={promote.busy ? undefined : onClose} role="presentation">
      <div className="gc-modal" role="dialog" aria-modal="true" aria-labelledby="gc-promote-title"
        onClick={(e) => e.stopPropagation()}>
        <div className="gc-modal-header">
          <h2 id="gc-promote-title">Make &ldquo;{conversation.name}&rdquo; a group chat?</h2>
          <button type="button" className="gc-modal-close" onClick={onClose} disabled={promote.busy}
            aria-label="Close">
            <FiX size={18} />
          </button>
        </div>
        <div className="gc-modal-body">{promote.form}</div>
        <div className="gc-modal-actions">
          <button type="button" className="gc-btn" onClick={onClose} disabled={promote.busy}>Cancel</button>
          <button type="button" className="gc-btn gc-btn--primary" onClick={promote.confirm}
            disabled={!promote.canConfirm} title={promote.empty ? 'Choose at least one person' : undefined}>
            {promote.confirmLabel}
          </button>
        </div>
      </div>
    </div>
  );
}
